// The browser's calls to the Quote Brain server job (backend
// src/routes/quote-brain-runs.js). apiFetch camelises top-level keys only;
// the run and candidate objects are built camelCase by the route itself.

import { apiFetch } from './data-service-api.js';
import { runProgress } from '../../backend/src/shared/quote-brain/run-progress.js';

export type QuoteBrainMode = 'off' | 'shadow' | 'on';

export interface QuoteBrainStatus {
    mode: QuoteBrainMode;
    refusal: { error: string; message: string } | null;
    /** The brain can write for this business: its profile and licence state serve the QuoteMaker Method. */
    ready: boolean;
}

export interface QuoteBrainRun {
    id: string;
    projectId: string;
    status: 'queued' | 'preparing' | 'running' | 'paused' | 'complete' | 'held' | 'failed' | 'cancelled';
    gateMode: 'shadow' | 'on';
    /** The cost viewer's only (showCosts, the route's showCostsFor): absent for everyone else. */
    budgetUsd?: number;
    spendUsd?: number;
    turnCount?: number;
    stopRule: string | null;
    /** For a business, the stop in plain words (run-status-text.js); the operator gets the run's own reason. */
    stopReason: string | null;
    stopRequestedAt: string | null;
    createdAt: string | null;
    startedAt: string | null;
    finishedAt: string | null;
    updatedAt: string | null;
    /** Set when this run stamped a fresh job as a Quote Brain job. */
    pipelineStampedAt: string | null;
    /** Set when the builder put that job back on the old pipeline. */
    releasedAt: string | null;
}

export interface QuoteBrainRunDetail {
    run: QuoteBrainRun;
    /** True for the cost viewer only: then the run carries its spend, budget and turns. */
    showCosts?: boolean;
    /** How far the run has got, for everyone (backend shared/quote-brain/run-progress.js). Absent from an older backend. */
    progress?: QuoteBrainRunProgress;
    candidate: null | {
        status: string;
        delivery: 'written' | 'held' | 'shadow' | null;
        holdReason: string | null;
        writtenUpdatedAt: string | null;
        decision: 'adopted' | 'kept_mine' | null;
        findings: any[];
        unresolved: any[];
    };
    /** The cost viewer's only: empty for everyone else, who read `progress`. */
    turns: Array<{ turn: number; tools: string[]; complete: boolean }>;
    /** While the run writes: the quote so far, from its latest finished turn (lucid 019 O2). Null otherwise. */
    working?: QuoteBrainWorking | null;
}

export interface QuoteBrainRunProgress {
    /** The furthest step of the method the run's tool calls reached. */
    reached: 'evidence' | 'scope' | 'pricing';
    /** What its latest call with a tool was doing; 'thinking' after turns with none; null before the first turn. */
    doing: string | null;
    /** At Pricing, the latest call wrote, changed or removed an item: a fix, then it prices again. */
    fixing: boolean;
}

/** The run's progress as the server sent it, or worked out from the turns a cost viewer gets. */
export function runProgressOf(detail: QuoteBrainRunDetail | null | undefined): QuoteBrainRunProgress {
    return detail?.progress || (runProgress(detail?.turns || []) as QuoteBrainRunProgress);
}

/**
 * The quote so far (backend write-project.js workingRows): the rows the write would make if the run ended at that turn.
 * Numbers and amounts (inc GST) only while the whole list is priced as it stands; before that, the items' titles alone.
 */
export interface QuoteBrainWorking {
    /** The turn it was read at: the cost viewer's only, as the turn count is. */
    turn?: number;
    /** 'quote' when the finished run would write straight into the job; 'new' when it waits for the compare panel. */
    version?: 'quote' | 'new';
    priced: boolean;
    rows: Array<{ code: string | null; title: string; amount: number | null; itemId: string | null }>;
    total: number | null;
}

export const LIVE_RUN_STATUSES = new Set(['queued', 'preparing', 'running']);

export function getQuoteBrainStatus(): Promise<QuoteBrainStatus> {
    return apiFetch('/brain/runs/status');
}

export function startQuoteBrainRun(projectId: string): Promise<{ run: QuoteBrainRun; showCosts?: boolean }> {
    return apiFetch('/brain/runs', { method: 'POST', body: JSON.stringify({ projectId }) });
}

/**
 * Whether the server's live tick starts the job's run by itself (backend
 * quote-brain-live/metrics.js liveStartState): 'expected' once its summary is
 * made, 'waiting' for its files or the next tick, 'retrying' after a failed
 * start (it tries each minute), 'gave_up', each of those two with the reason,
 * 'started', or 'no'. Absent from a backend older than this field.
 */
export interface QuoteBrainAutoStart {
    state: 'expected' | 'waiting' | 'retrying' | 'gave_up' | 'started' | 'no';
    reason: string | null;
}

/**
 * Stage 1 has read the job's files and written its summary: the server's own
 * start for this job runs now, rather than on its next minute. Returns the
 * start state as it then stands ('started', 'waiting' for files still
 * uploading, or 'expected' when the server has no summary for the job).
 */
/** The server's start for one job. `requested`: a Quote Run asks for it, so it is picked up whatever its age (D2). */
export function startQuoteBrainLiveNow(projectId: string, { requested = false }: { requested?: boolean } = {}): Promise<{ autoStart?: QuoteBrainAutoStart }> {
    return apiFetch('/brain/runs/live-start', { method: 'POST', body: JSON.stringify(requested ? { projectId, requested: true } : { projectId }) });
}

export function listQuoteBrainRuns(projectId: string): Promise<{ runs: QuoteBrainRun[]; autoStart?: QuoteBrainAutoStart; showCosts?: boolean }> {
    return apiFetch(`/brain/runs?projectId=${encodeURIComponent(projectId)}`);
}

/** A quote QuoteMaker wrote ('written'), or a new version it held beside one ('held'), for the builder's notices. */
export interface QuoteMakerWrittenRun {
    runId: string;
    projectId: string;
    title: string;
    delivery: 'written' | 'held';
    deliveredAt: string;
}

/** The quotes written since `since` (the last answer's `now`); with no `since`, only the server's `now`. */
export function listQuoteMakerWritten(since: string | null): Promise<{ now: string; runs: QuoteMakerWrittenRun[] }> {
    return apiFetch(`/brain/runs/written${since ? `?since=${encodeURIComponent(since)}` : ''}`);
}

export function getQuoteBrainRun(runId: string): Promise<QuoteBrainRunDetail> {
    return apiFetch(`/brain/runs/${encodeURIComponent(runId)}`);
}

export function stopQuoteBrainRun(runId: string): Promise<{ run: QuoteBrainRun }> {
    return apiFetch(`/brain/runs/${encodeURIComponent(runId)}/stop`, { method: 'POST', body: '{}' });
}

export function continueQuoteBrainRun(runId: string): Promise<{ run: QuoteBrainRun }> {
    return apiFetch(`/brain/runs/${encodeURIComponent(runId)}/continue`, { method: 'POST', body: '{}' });
}

export type QuoteBrainRowMark = 'locked' | 'edited' | 'added' | 'unchanged' | 'not-tracked' | 'statutory' | 'statutory-typed';

export interface QuoteBrainComparisonRow { id: string; code: string; title: string; amount: number }

/** What the Adopt / Keep mine panel shows (write-project.js comparison()). */
export interface QuoteBrainComparison {
    runId: string;
    projectId: string;
    status: string;
    delivery: 'written' | 'held' | 'shadow' | null;
    decision: 'adopted' | 'kept_mine' | null;
    holdReason: string | null;
    current: { rows: Array<QuoteBrainComparisonRow & { keep: boolean; reason: QuoteBrainRowMark }>; total: number; gstInclusive?: boolean };
    /** Why Adopt is refused right now, or null. */
    adoptBlocked?: { code: string; message: string } | null;
    /** The brain's rows; coveredByYours marks one a kept row of yours covers, which Adopt leaves out. */
    candidate: { rows: Array<QuoteBrainComparisonRow & { itemId?: string; coveredByYours?: boolean }>; total: number; optional: Array<{ title: string; kind: string; amount: number }> };
    afterAdopt: { rows: Array<QuoteBrainComparisonRow & { kept: boolean }>; total: number };
    findings: Array<{ code: string; message: string; itemId?: string; blocking?: boolean }>;
    confirms: Array<{ id: string; question: string; assumption: string; itemId?: string }>;
    lanesUsed: any[];
    /** What Adopt clears besides the list (the run's write clears both). */
    replaces?: { workMethod: boolean; costingSheet: boolean };
}

export function getQuoteBrainComparison(runId: string): Promise<QuoteBrainComparison> {
    return apiFetch(`/brain/runs/${encodeURIComponent(runId)}/comparison`);
}

export function adoptQuoteBrainRun(runId: string, expectedUpdatedAt: string): Promise<{ adopted: boolean; updatedAt: string | null; kept: number | null }> {
    return apiFetch(`/brain/runs/${encodeURIComponent(runId)}/adopt`, { method: 'POST', body: JSON.stringify({ expectedUpdatedAt }) });
}

export function keepMineQuoteBrainRun(runId: string): Promise<{ keptMine: boolean; released: boolean }> {
    return apiFetch(`/brain/runs/${encodeURIComponent(runId)}/keep-mine`, { method: 'POST', body: '{}' });
}

export function releaseQuoteBrainRun(runId: string): Promise<{ released: boolean }> {
    return apiFetch(`/brain/runs/${encodeURIComponent(runId)}/release`, { method: 'POST', body: '{}' });
}

/** What the run is doing, in the builder's words, from what its latest call was doing (run-progress.js). */
export function runStepLabel(detail: QuoteBrainRunDetail | null): string {
    const run = detail?.run;
    if (!run) return 'starting';
    if (run.status === 'queued') return 'waiting for the server';
    if (run.status === 'preparing') return 'gathering the evidence';
    return runProgressOf(detail).doing || 'starting';
}
